/**
 * highlight.js — Highlight overlays on the docx-preview rendered output
 *
 * Creates colored <span> wrappers around issue text in the preview DOM.
 * Open issues get yellow highlights with hover badges; accepted issues
 * get green highlights.
 *
 * ─── PARAGRAPH MAPPING STRATEGY ───
 *
 * Maps parsed paragraph IDs to rendered <p> elements in priority order:
 *   1. data-para-id attribute (injected by docx.injectParagraphIds)
 *   2. Exact normalized text match
 *   3. Prefix-based text match (first 50 chars)
 *   4. Substring containment match
 *
 * ─── ANCHOR MODEL ───
 *
 * Each issue carries an "anchor" object: { before, after, needle }
 *   - needle: the text to highlight (original for open, suggested for accepted)
 *   - before: ~40 chars of paragraph text before the needle
 *   - after:  ~40 chars of paragraph text after the needle
 *
 * Anchors disambiguate when the same text appears multiple times in a
 * paragraph (e.g. two occurrences of "the University").
 */
import { normalizeText } from './docx';

/* ── Inject hover CSS once ── */
const STYLE_ID = 'hl-hover-styles';
function ensureHoverStyles() {
  // Skip if style was already injected.
  if (document.getElementById(STYLE_ID)) return;
  // Create style node for hover badge behavior.
  const style = document.createElement('style');
  // Assign deterministic id for dedup checks.
  style.id = STYLE_ID;
  // CSS rules: badges hidden by default, visible on hover.
  style.textContent = `
    [data-hl="true"] [data-hl-badge] {
      opacity: 0;
      transition: opacity 0.15s ease;
      pointer-events: none;
    }
    [data-hl="true"]:hover [data-hl-badge] {
      opacity: 1;
    }
  `;
  // Attach once to document head.
  document.head.appendChild(style);
}

/* ══════════════════════════════════════════════════════════════
 *  DOM utilities
 * ══════════════════════════════════════════════════════════════ */

/** Remove all highlight <span> wrappers, restoring original text nodes. */
export function clearPreviewHighlights(container) {
  // Guard when preview root is missing.
  if (!container) return;
  // Select all existing highlight wrapper spans.
  const marks = container.querySelectorAll("[data-hl='true']");
  marks.forEach((m) => {
    // Parent needed to unwrap children.
    const parent = m.parentNode;
    // Skip detached wrappers safely.
    if (!parent) return;
    // Remove badge elements BEFORE unwrapping — they're decorative overlays,
    // not real document text. If left in, they leak into the paragraph and
    // corrupt text-node maps ("markets, and" → "markets, and3"), breaking
    // all subsequent anchor searches.
    m.querySelectorAll('[data-hl-badge]').forEach((b) => b.remove());
    // Move all wrapped child nodes back into parent in-place.
    while (m.firstChild) parent.insertBefore(m.firstChild, m);
    // Remove empty wrapper span.
    parent.removeChild(m);
    // Merge adjacent text nodes after surgery.
    parent.normalize();
  });
}

/**
 * Walk all text nodes under rootEl and build a concatenated string
 * with a mapping from char offsets back to DOM text nodes.
 */
function buildTextNodeMap(rootEl) {
  // Walk only text nodes under root element.
  const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, {
    // Accept nodes that actually contain text.
    acceptNode: (node) => (node.nodeValue ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
  });

  // Offset map: each entry tracks one text node range in concatenated text.
  const nodes = [];
  // Concatenated paragraph text.
  let text = '';
  while (walker.nextNode()) {
    // Current text node.
    const n = walker.currentNode;
    // Start offset before appending this node's text.
    const start = text.length;
    // Append text-node value.
    text += n.nodeValue;
    // Record node and its [start,end) offsets.
    nodes.push({ node: n, start, end: text.length });
  }
  // Return both concatenated text and node-offset map.
  return { text, nodes };
}

/** Create a DOM Range spanning [start, end) within a text-node map. */
function rangeFromOffsets(textMap, start, end) {
  // Pull mapped text-node segments.
  const { nodes } = textMap;
  // Segment containing start offset.
  let startNode = null;
  // Segment containing end offset.
  let endNode = null;

  for (const n of nodes) {
    // First segment crossing start boundary.
    if (!startNode && n.end > start) startNode = n;
    // First segment reaching end boundary.
    if (n.end >= end) {
      endNode = n;
      break;
    }
  }
  // Invalid range when boundaries cannot be located.
  if (!startNode || !endNode) return null;

  try {
    // Native DOM Range over resolved boundaries.
    const r = document.createRange();
    // Convert global offset to start-node local offset.
    r.setStart(startNode.node, Math.max(0, start - startNode.start));
    // Convert global offset to end-node local offset.
    r.setEnd(endNode.node, Math.max(0, end - endNode.start));
    // Return constructed range.
    return r;
  } catch {
    // Fail gracefully on stale/invalid DOM references.
    return null;
  }
}

/** Wrap a DOM Range with a styled highlight <span>. */
function wrapRange(range, issueId, number, kind) {
  // Create highlight wrapper element.
  const span = document.createElement('span');
  // Marker attribute for cleanup/selectors.
  span.setAttribute('data-hl', 'true');
  // Store issue id for click routing.
  span.setAttribute('data-issue-id', issueId);
  // Store highlight type (open/accepted).
  span.setAttribute('data-hl-kind', kind || 'open');
  span.setAttribute('role', 'button');
  span.setAttribute('tabindex', '0');
  span.setAttribute(
    'aria-label',
    kind === 'accepted'
      ? 'Open applied change'
      : `Open suggestion${number != null ? ` ${number}` : ''}`,
  );

  span.style.background =
    kind === 'accepted' ? 'rgba(40, 167, 69, 0.22)' : 'rgba(255, 230, 0, 0.45)';
  span.style.borderRadius = '4px';
  span.style.padding = '0 2px';
  span.style.cursor = 'pointer';
  span.style.position = 'relative';
  span.style.display = 'inline';

  try {
    // Move selected contents into wrapper.
    span.appendChild(range.extractContents());
  } catch {
    // Extraction can fail if range became invalid.
    return null;
  }

  // Number badge (open issues only, visible on hover via CSS)
  if (kind === 'open' && number != null) {
    // Create numeric badge for open suggestions.
    const badge = document.createElement('span');
    // Badge text is suggestion ordinal.
    badge.textContent = String(number);
    badge.setAttribute('data-hl-badge', 'true');
    Object.assign(badge.style, {
      position: 'absolute',
      left: '-14px',
      top: '-10px',
      fontSize: '11px',
      fontWeight: '800',
      lineHeight: '16px',
      height: '16px',
      minWidth: '16px',
      padding: '0 4px',
      borderRadius: '999px',
      background: 'rgba(0,0,0,0.75)',
      color: 'white',
      textAlign: 'center',
      pointerEvents: 'none',
    });
    // Mount badge inside highlight wrapper.
    span.appendChild(badge);
  }

  // Insert wrapper at original range location.
  range.insertNode(span);
  // Return created highlight element.
  return span;
}

/* ══════════════════════════════════════════════════════════════
 *  Paragraph mapping
 * ══════════════════════════════════════════════════════════════ */

/**
 * Map parsed paragraph IDs to rendered <p> DOM elements.
 * Uses a 4-pass strategy (attribute → exact → prefix → substring).
 */
export function mapParagraphsToPreviewPs(previewRoot, extractedParagraphs) {
  // Collect rendered paragraph tags from preview output.
  const previewPs = Array.from(previewRoot.querySelectorAll('p'));
  // paragraphId -> DOM <p> mapping.
  const mapping = new Map();
  // Tracks preview paragraph indices already mapped.
  const claimed = new Set();

  const previewData = previewPs.map((el, idx) => ({
    el,
    idx,
    text: normalizeText(el.innerText || el.textContent || ''),
  }));

  // Pass 1: data-para-id attribute (deterministic, preferred)
  for (const pd of previewData) {
    // Deterministic mapping if data-para-id exists.
    const attr = pd.el.getAttribute('data-para-id');
    if (attr != null) {
      const pid = parseInt(attr, 10);
      if (!isNaN(pid) && extractedParagraphs[pid]) {
        mapping.set(pid, pd.el);
        // Prevent same preview paragraph from being reused.
        claimed.add(pd.idx);
      }
    }
  }

  const needTextMatch = extractedParagraphs.filter(
    (p) => !mapping.has(p.paragraphId) && normalizeText(p.text),
  );
  // Early exit when attribute pass mapped everything.
  if (needTextMatch.length === 0) return mapping;

  // Pass 2–4: text-based fallbacks
  const passes = [
    // Pass 2: Exact normalized text
    (want, got) => got === want,
    // Pass 3: Prefix match (first 50 chars)
    (want, got) => {
      if (want.length < 5) return false;
      const sig = want.slice(0, 50);
      return got.startsWith(sig) || want.startsWith(got.slice(0, 50));
    },
    // Pass 4: Substring containment
    (want, got) => {
      if (want.length < 10 || got.length < 10) return false;
      const chunk = want.slice(0, 80);
      return got.includes(chunk) || want.includes(got.slice(0, 80));
    },
  ];

  for (const matchFn of passes) {
    // Keep forward matching order to reduce wrong assignments.
    let searchStart = 0;
    for (const p of needTextMatch) {
      // Skip already matched paragraph IDs.
      if (mapping.has(p.paragraphId)) continue;
      const want = normalizeText(p.text);
      // Ignore empty normalized text.
      if (!want) continue;

      for (let j = searchStart; j < previewData.length; j++) {
        // Skip paragraphs already claimed by prior matches.
        if (claimed.has(j)) continue;
        if (matchFn(want, previewData[j].text)) {
          // Save mapping when this pass strategy matches.
          mapping.set(p.paragraphId, previewData[j].el);
          claimed.add(j);
          // Continue future searches from next preview paragraph.
          searchStart = j + 1;
          break;
        }
      }
    }
  }

  // Best-effort mapping result after all fallback passes.
  return mapping;
}

/* ══════════════════════════════════════════════════════════════
 *  Anchor utilities
 * ══════════════════════════════════════════════════════════════ */

/**
 * Find text in a paragraph using an anchor's before/after context.
 * Returns { start, end } or null if not found.
 */
export function findByAnchor(pText, anchor) {
  // Anchor must include non-empty needle text.
  if (!anchor?.needle || anchor.needle.length === 0) return null;

  // Anchor context components.
  const { needle, before, after } = anchor;

  // Try context-windowed search first
  if (before) {
    // Locate before-context in paragraph.
    const bIdx = pText.indexOf(before);
    if (bIdx !== -1) {
      // Search begins immediately after before-context.
      const searchFrom = bIdx + before.length;
      // Default upper bound is paragraph end.
      let searchTo = pText.length;
      if (after) {
        // If after-context exists, bound search window before it.
        const aIdx = pText.indexOf(after, searchFrom);
        if (aIdx !== -1) searchTo = aIdx;
      }
      // Window constrained by before/after contexts.
      const window = pText.slice(searchFrom, searchTo);
      // Find needle inside context window.
      const rel = window.indexOf(needle);
      if (rel !== -1) {
        // Convert relative offset to paragraph-global offset.
        const start = searchFrom + rel;
        // Return resolved half-open range.
        return { start, end: start + needle.length };
      }
    }
  }

  // Fallback: direct search
  // First occurrence fallback when contextual search fails.
  const directIdx = pText.indexOf(needle);
  if (directIdx !== -1) return { start: directIdx, end: directIdx + needle.length };

  // Anchor-based resolution failed.
  return null;
}

/**
 * Build an anchor context object for an issue.
 *
 * @param {string} paragraphText - Full paragraph text
 * @param {number} start         - Start offset of the needle
 * @param {number} end           - End offset of the needle
 * @param {string} needle        - The text being highlighted
 * @returns {{ before: string, after: string, needle: string }}
 */
export function buildAnchor(paragraphText, start, end, needle) {
  // Return before/after context slices + canonical needle text.
  return {
    // Up to 40 chars before the issue span.
    before: paragraphText.slice(Math.max(0, start - 40), start),
    // Up to 40 chars after the issue span.
    after: paragraphText.slice(end, Math.min(paragraphText.length, end + 40)),
    // Needle text used for future anchor matching.
    needle,
  };
}

/* ══════════════════════════════════════════════════════════════
 *  Selection styling
 * ══════════════════════════════════════════════════════════════ */

/** Apply or remove the "selected" visual style on a highlight span. */
export function applySelectedStyle(el, selected) {
  // Guard null element.
  if (!el) return;
  // Preserve type-specific default color when deselecting.
  const kind = el.getAttribute?.('data-hl-kind') || 'open';

  if (selected) {
    // Stronger selected visual style.
    el.style.outline = '2px solid rgba(0, 122, 255, 0.95)';
    el.style.background = 'rgba(0, 122, 255, 0.18)';
  } else {
    // Clear selected border.
    el.style.outline = 'none';
    // Restore default status-specific highlight color.
    el.style.background =
      kind === 'accepted' ? 'rgba(40, 167, 69, 0.22)' : 'rgba(255, 230, 0, 0.45)';
  }
}

/** Deselect all highlights, then select the given one. */
export function highlightSelected(issueId, highlightMap) {
  // Clear selected state from all highlights first.
  for (const [, node] of highlightMap.entries()) applySelectedStyle(node, false);
  // Resolve highlight for requested issue id.
  const el = highlightMap.get(issueId);
  // Apply selected style when found.
  if (el) applySelectedStyle(el, true);
}

/* ══════════════════════════════════════════════════════════════
 *  Main entry point — apply all highlights to preview
 * ══════════════════════════════════════════════════════════════ */

/**
 * Clear existing highlights and re-apply for all open + accepted issues.
 *
 * @returns {Map<string, HTMLElement>} issueId → highlight <span> element
 */
export function applyHighlightsToPreview(previewRoot, paragraphs, issues, issueNumberById) {
  // Ensure badge hover CSS exists.
  ensureHoverStyles();
  // Remove stale wrappers from prior render pass.
  clearPreviewHighlights(previewRoot);

  // Only open/accepted statuses should be highlighted.
  const highlightable = issues.filter((i) => i.status === 'open' || i.status === 'accepted');
  // No highlights to apply.
  if (!highlightable.length) return new Map();

  // Map logical paragraph ids to rendered DOM paragraphs.
  const pMap = mapParagraphsToPreviewPs(previewRoot, paragraphs);
  // issueId -> highlight span map returned to caller.
  const highlightEls = new Map();

  // Group by paragraph for efficient processing
  const byPara = new Map();
  for (const issue of highlightable) {
    // Skip issues whose paragraph could not be mapped.
    const pEl = pMap.get(issue.paragraphId);
    if (!pEl) continue;
    // Initialize paragraph bucket lazily.
    if (!byPara.has(issue.paragraphId)) byPara.set(issue.paragraphId, []);
    // Push issue into its paragraph bucket.
    byPara.get(issue.paragraphId).push(issue);
  }

  for (const [, paraIssues] of byPara.entries()) {
    // Any issue in bucket can provide paragraph id.
    const pEl = pMap.get(paraIssues[0].paragraphId);
    if (!pEl) continue;

    // Build live text-node map from current paragraph DOM.
    const textMap = buildTextNodeMap(pEl);
    // Concatenated paragraph text for anchor searching.
    const pText = textMap.text;

    // Resolve anchor positions
    const matches = [];
    for (const issue of paraIssues) {
      // Status-driven highlight kind.
      const kind = issue.status === 'accepted' ? 'accepted' : 'open';
      // Select anchor field based on status.
      const anchor = kind === 'accepted' ? issue.anchorAccepted : issue.anchorOpen;
      // Skip when anchor missing.
      if (!anchor) continue;

      // Resolve start/end by anchor context.
      const m = findByAnchor(pText, anchor);
      // Skip missing/zero-length resolutions.
      if (!m || m.start === m.end) continue; // skip zero-length matches
      // Queue resolved match.
      matches.push({ issueId: issue.id, start: m.start, end: m.end, kind });
    }

    // Remove overlapping matches (keep earlier ones)
    matches.sort((a, b) => a.start - b.start);
    const nonOverlap = [];
    let lastEnd = -1;
    for (const m of matches) {
      // Keep only first non-overlapping ranges in order.
      if (m.start >= lastEnd) {
        nonOverlap.push(m);
        lastEnd = m.end;
      }
    }

    // Wrap ranges in reverse order (so earlier DOM positions stay valid)
    nonOverlap.sort((a, b) => b.start - a.start);
    for (const m of nonOverlap) {
      // Convert offsets to DOM range.
      const r = rangeFromOffsets(textMap, m.start, m.end);
      if (!r) continue;

      // Badge number applies to open highlights only.
      const num = m.kind === 'open' ? issueNumberById?.get(m.issueId) : null;
      // Wrap DOM range with styled highlight span.
      const el = wrapRange(r, m.issueId, num, m.kind);
      // Keep reference for selection/popover anchoring.
      if (el) highlightEls.set(m.issueId, el);
    }
  }

  // Return map for issue selection and popover logic.
  return highlightEls;
}
